import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Stack,
  Tag,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Timestamp, doc, updateDoc } from 'firebase/firestore';
import { ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FiCheck, FiDollarSign, FiLogIn, FiLogOut } from 'react-icons/fi';
import * as yup from 'yup';
import InputPesos from '~/components/Inputs/InputPesos';
import Tooltip from '~/components/Tooltip';
import firebaseFirestore from '~/config/firebase/firestore';
import { etiquetaEstado, idDeEmpresa, registrarEvento } from '~/core/tracking/reservationEvents';
import { Payment } from '~/core/types';
import { useAuth } from '~/hooks/useAuth';
import { formatoPesos } from '~/utils/dinero';

/**
 * Cambiar el estado de una reserva: confirmar que se vio, check-in, check-out
 * con el valor vendido (que puede ser cero) y deshacer el check-in.
 *
 * Vivía dentro de la tabla de reservas. Se saca aquí, tal cual, para que el
 * calendario ofrezca EXACTAMENTE los mismos botones, con los mismos colores,
 * textos y rastro en el historial. Un solo sitio, un solo comportamiento.
 */

export type PopOverChangeStatusProps = {
  /** La reserva entera: hace falta para dejar rastro de quién cambia el estado. */
  reserva: any;
  reservationId: string;
  name: string;
  status: string;
  datetime: Timestamp;
  statusHistory?: any;
  numberPeople: number;
  payment: Payment;
  onChanged?: (reservationId: string) => void;
  /**
   * Solo el icono, con el texto del botón como aviso al pasar el ratón. Para
   * las tarjetas del calendario, donde una franja de media hora mide 24 px.
   * Sin esto, el botón es el de la tabla, tal cual.
   */
  compacto?: boolean;
  /**
   * El contenido del popover sale por un `Portal`. Hace falta cuando el botón
   * vive dentro de una caja con `overflow: hidden` (las tarjetas), donde el
   * popover en línea quedaría recortado. La tabla no lo necesita.
   */
  enPortal?: boolean;
};

/** Sitio donde se pinta el contenido del popover: en línea o en un Portal. */
const Salida = ({ enPortal, children }: { enPortal?: boolean; children: ReactNode }) =>
  enPortal ? <Portal>{children}</Portal> : <>{children}</>;

/**
 * El botón compacto: el mismo color y el mismo texto que el de la tabla, pero
 * el texto va en el aviso al pasar el ratón y en el `aria-label`.
 *
 * Se envuelve en una caja porque el tooltip y el `PopoverTrigger` de Chakra
 * se enganchan cada uno a su hijo directo, y no pueden compartir el mismo.
 */
const ConAviso = ({ etiqueta, children }: { etiqueta: string; children: ReactNode }) => (
  <Tooltip label={etiqueta} placement='top' hasArrow openDelay={300}>
    <Box display='inline-flex' lineHeight={0}>
      {children}
    </Box>
  </Tooltip>
);

const COMPACTO = { size: 'xs', h: '20px', minW: '20px', fontSize: '12px', borderRadius: '4px' } as const;

export const PopOverChangeStatus = ({ status, ...otherProps }: PopOverChangeStatusProps) => {
  if (status === 'checkin' || status === 'checkout') {
    return <PopOverCheckout {...otherProps} status={status} />;
  } else if (status == 'cancelled') {
    return <Tag>Cancelled</Tag>;
  }
  return <PopOverCheckin {...otherProps} />;
};

/** Deja constancia de un cambio de estado hecho desde el panel. */
export const registrarCambioEstado = async (
  reserva: any,
  usuario: any,
  antes: string,
  despues: string
) => {
  await registrarEvento({
    tipo: 'status',
    reservationId: reserva?.id,
    code: reserva?.code,
    companyId: usuario?.company?.id ?? idDeEmpresa(reserva?.location?.company),
    locationId: reserva?.location?.id,
    locationName: reserva?.location?.name,
    usuario,
    origen: { esPanel: true, from: 'sistema' },
    cambios: [{ field: 'status', before: antes, after: despues }],
    cliente: {
      nombre: reserva?.namesAndSurnames ?? reserva?.name,
      correo: reserva?.email,
    },
  });
};

const PopOverCheckin = ({
  reserva,
  reservationId,
  statusHistory,
  name,
  numberPeople,
  compacto,
  enPortal,
}: Partial<PopOverChangeStatusProps>) => {
  const { user } = useAuth();
  const handleCheckIn = async () => {
    const reservationRef = doc(firebaseFirestore, 'reservations', reservationId!);
    await updateDoc(reservationRef, {
      status: 'checkin',
      statusHistory: [
        ...statusHistory,
        { status: 'checkin', createdAt: new Date(), user: user?.email ?? '' },
      ],
    });
    await registrarCambioEstado(reserva, user, 'confirmada', 'check-in');
  };
  const { isOpen, onToggle, onClose } = useDisclosure();
  return (
    <Popover
      isLazy
      variant={'responsive'}
      returnFocusOnClose={false}
      isOpen={isOpen}
      onClose={onClose}
      placement='bottom'
      closeOnBlur
    >
      {compacto ? (
        <ConAviso etiqueta='Hacer Checkin'>
          <PopoverTrigger>
            <IconButton
              {...COMPACTO}
              aria-label='Hacer Checkin'
              icon={<FiLogIn />}
              variant='outline'
              colorScheme='blue'
              bg='white'
              onClick={onToggle}
            />
          </PopoverTrigger>
        </ConAviso>
      ) : (
        <PopoverTrigger>
          <Button
            textColor={'blue.500'}
            borderColor='blue.500'
            borderWidth={1}
            size={'xs'}
            onClick={onToggle}
          >
            Hacer Checkin
          </Button>
        </PopoverTrigger>
      )}
      <Salida enPortal={enPortal}>
      <PopoverContent>
        <PopoverHeader fontWeight='semibold'>Check in</PopoverHeader>
        <PopoverArrow onClick={onToggle} />
        <PopoverCloseButton />
        <PopoverBody>
          <VStack alignItems={'flex-start'}>
            <Text>Id: {reservationId}</Text>
            <Text>Nombre: {name}</Text>
            <Text># personas {numberPeople}</Text>
          </VStack>
        </PopoverBody>
        <PopoverFooter display='flex' justifyContent='flex-end'>
          <ButtonGroup size='sm'>
            <Button onClick={onToggle} variant='outline'>
              Cerrar
            </Button>
            <Button onClick={handleCheckIn} colorScheme='orange'>
              Aceptar
            </Button>
          </ButtonGroup>
        </PopoverFooter>
      </PopoverContent>
      </Salida>
    </Popover>
  );
};

// El campo entrega solo cifras ("180000"); yup las convierte en el número que
// se guarda (180000), igual que antes. Los puntos son solo de pantalla.
const schema = yup.object({
  amount: yup
    .number()
    .typeError('Escribe el valor (puede ser 0)')
    .min(0, 'Debe ser un valor mayor o igual a 0')
    .max(9999999, 'Debe ser un valor menor a $10.000.000')
    .required('Requerido'),
});

const PopOverCheckout = ({
  reserva,
  reservationId,
  name,
  numberPeople,

  status,
  statusHistory,
  payment,
  compacto,
  enPortal,
}: Partial<PopOverChangeStatusProps>) => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const { user } = useAuth();

  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm<{ amount: string }>({
    mode: 'onBlur',
    resolver: yupResolver(schema),
    defaultValues: {
      amount: payment?.amount.toString() ?? '',
    },
  });

  const handleCheckOut = async ({ amount }: { amount: string }) => {
    const reservationRef = doc(firebaseFirestore, 'reservations', reservationId!);
    await updateDoc(reservationRef, {
      status: 'checkout',
      statusHistory: [
        ...statusHistory,
        { status: 'checkout', createdAt: new Date(), user: user?.email ?? '' },
      ],
      payment: { amount, createdAt: new Date() },
    });
    await registrarCambioEstado(reserva, user, 'check-in', 'check-out');
    onToggle();
  };

  const handleRemoveCheckin = async () => {
    const reservationRef = doc(firebaseFirestore, 'reservations', reservationId!);
    await updateDoc(reservationRef, {
      status: 'confirmed',
      payment: {
        amount: 0,
      },
      statusHistory: [
        ...statusHistory,
        { status: 'confirmed', createdAt: new Date(), user: user?.email ?? '' },
      ],
    });
    await registrarCambioEstado(reserva, user, 'check-in', 'confirmada (se deshizo el check-in)');
    onToggle();
  };

  return (
    <Popover
      isLazy
      variant={'responsive'}
      returnFocusOnClose={false}
      isOpen={isOpen}
      onClose={onClose}
      placement='bottom'
      closeOnBlur
    >
      {compacto ? (
        <ConAviso
          etiqueta={status == 'checkin' ? 'Hacer Checkout' : `Completado - ${formatoPesos(payment?.amount)}`}
        >
          <PopoverTrigger>
            {status == 'checkin' ? (
              <IconButton
                {...COMPACTO}
                aria-label='Hacer Checkout'
                icon={<FiLogOut />}
                onClick={onToggle}
                backgroundColor='orange'
                textColor='white'
                colorScheme='orange'
              />
            ) : (
              <IconButton
                {...COMPACTO}
                aria-label={`Completado - ${formatoPesos(payment?.amount)}`}
                icon={<FiDollarSign />}
                onClick={onToggle}
                colorScheme='purple'
              />
            )}
          </PopoverTrigger>
        </ConAviso>
      ) : (
        <PopoverTrigger>
          {status == 'checkin' ? (
            <Button
              size={'xs'}
              onClick={onToggle}
              backgroundColor={status == 'checkin' ? 'orange' : 'orange.200'}
              textColor={status == 'checkin' ? 'white' : 'gray.600'}
              borderColor={status == 'checkin' ? 'orange' : 'gray.600'}
              borderWidth={status == 'checkin' ? 0 : 1}
              colorScheme='orange'
            >
              Hacer Checkout
            </Button>
          ) : (
            <Button size={'xs'} onClick={onToggle} colorScheme='purple'>
              Completado - {formatoPesos(payment?.amount)}
            </Button>
          )}
        </PopoverTrigger>
      )}
      <Salida enPortal={enPortal}>
      <PopoverContent>
        <form onSubmit={handleSubmit(handleCheckOut)}>
          <PopoverHeader fontWeight='semibold'>Checkout</PopoverHeader>
          <PopoverArrow onClick={onToggle} />
          <PopoverCloseButton />
          <PopoverBody>
            <VStack alignItems={'flex-start'}>
              <Button size={'xs'} onClick={handleRemoveCheckin} colorScheme='red'>
                Deshacer checkin
              </Button>
              <Text>Id: {reservationId}</Text>
              <Text>Nombre: {name}</Text>
              <Text mb={4}># personas {numberPeople}</Text>

              <Stack spacing={4}>
                <Box py={4}>
                  <FormControl isInvalid={Boolean(errors.amount)}>
                    <FormLabel htmlFor={`amount-${reservationId}`}>Total de consumo*</FormLabel>
                    <Controller
                      name='amount'
                      control={control}
                      render={({ field }) => (
                        <InputPesos
                          id={`amount-${reservationId}`}
                          name={field.name}
                          ref={field.ref}
                          value={field.value}
                          onChange={(cifras) => {
                            field.onChange(cifras);
                            // Con el aviso a la vista, se revalida al escribir: si
                            // esperara a salir del campo, el aviso se iría justo al
                            // pulsar «Aceptar», el botón subiría y el clic se perdería.
                            if (errors.amount) trigger('amount');
                          }}
                          onBlur={field.onBlur}
                          data-testid='total-consumo'
                        />
                      )}
                    />
                    <FormErrorMessage>{errors.amount?.message}</FormErrorMessage>
                  </FormControl>
                </Box>
              </Stack>
            </VStack>
          </PopoverBody>
          <PopoverFooter display='flex' justifyContent='flex-end'>
            <ButtonGroup size='sm'>
              <Button onClick={onToggle} variant='outline'>
                Cerrar
              </Button>
              <Button type='submit' colorScheme='orange'>
                Aceptar
              </Button>
            </ButtonGroup>
          </PopoverFooter>
        </form>
      </PopoverContent>
      </Salida>
    </Popover>
  );
};


/**
 * "Confirmar vista": la reserva pasa de pendiente a confirmada.
 *
 * Es el botón verde de la tabla, que estaba escrito en línea dentro de la
 * fila. Ahora es una pieza para poder ponerla también en el calendario.
 */
export const BotonConfirmarVista = ({
  reserva,
  onChanged,
  compacto,
}: {
  reserva: any;
  onChanged?: (reservationId: string) => void;
  /** Solo el icono (ver `PopOverChangeStatusProps.compacto`). */
  compacto?: boolean;
}) => {
  const { user } = useAuth();
  const confirmar = async () => {
    const reservationRef = doc(firebaseFirestore, 'reservations', reserva.id!);
    await updateDoc(reservationRef, {
      status: 'confirmed',
      statusHistory: [
        ...(reserva?.statusHistory ?? []),
        // El historial guardaba el estado sin decir quién lo cambió; ahora sí.
        {
          status: 'confirmed',
          createdAt: new Date(),
          user: user?.email ?? '',
        },
      ],
    });
    await registrarEvento({
      tipo: 'status',
      reservationId: reserva.id!,
      code: reserva.code,
      companyId: user.company?.id,
      locationId: reserva.location?.id,
      locationName: reserva.location?.name,
      usuario: user,
      origen: { esPanel: true, from: 'sistema' },
      cambios: [
        {
          field: 'status',
          before: etiquetaEstado(reserva.status),
          after: 'confirmada',
        },
      ],
      cliente: {
        nombre: reserva.namesAndSurnames ?? reserva.name,
        correo: reserva.email,
      },
    });
    onChanged?.(reserva.id!);
  };
  if (compacto) {
    return (
      <ConAviso etiqueta='Confirmar vista'>
        <IconButton
          {...COMPACTO}
          aria-label='Confirmar vista'
          icon={<FiCheck />}
          colorScheme='green'
          onClick={confirmar}
        />
      </ConAviso>
    );
  }
  return (
    <Button onClick={confirmar} size={'xs'} colorScheme='green'>
      Confirmar vista
    </Button>
  );
};
